/**
 * DatabaseManager.js
 *
 * Admin-only UI for the tenant provisioning and FieldTicket init console.
 * All API calls go through the house-side proxy (api/db_manager.php), which
 * validates session, role, and scoped bearer token before forwarding to the
 * src console. No secrets ever touch this file.
 *
 * Token storage: sessionStorage key "og_access_token"
 * Token audience: "ogsupport-db-manager"
 * Token TTL:       10 minutes (refresh by logging out and back in)
 */

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faUsers,
  faList,
  faWrench,
  faPlus,
  faCheckCircle,
  faTimes,
  faExclamationTriangle,
  faSpinner,
  faCopy,
  faKey,
  faChevronDown,
  faChevronUp,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "ogcommon";

/* ── Constants ──────────────────────────────────────────────────────────── */

const API_BASE = "https://house.ogendeavors.com/api/db_manager.php";

const FIELDTICKET_DOCROOT = "public_html/ogfieldticket";
const FIELDTICKET_HOST_SUFFIX = ".ogfieldticket.ogpumper.net";

/* ── Token utilities ────────────────────────────────────────────────────── */

function b64urlDecode(str) {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (padded.length % 4)) % 4;
  return atob(padded + "=".repeat(padding));
}

function parseAccessToken() {
  const tok = sessionStorage.getItem("og_access_token") || "";
  if (!tok) return null;
  try {
    const inner = b64urlDecode(tok);
    const dotIdx = inner.indexOf(".");
    if (dotIdx === -1) return null;
    const claims = JSON.parse(b64urlDecode(inner.slice(0, dotIdx)));
    return claims;
  } catch {
    return null;
  }
}

function tokenStatus() {
  const claims = parseAccessToken();
  if (!claims) return "missing";
  const now = Math.floor(Date.now() / 1000);
  if (claims.exp && now > claims.exp + 30) return "expired";
  return "ok";
}

/* ── Shared sub-components ──────────────────────────────────────────────── */

function Spinner() {
  return (
    <FontAwesomeIcon
      icon={faSpinner}
      className="animate-spin text-blue-400"
    />
  );
}

function StepLog({ steps, theme }) {
  const isDark = theme === "dark";
  if (!Array.isArray(steps) || steps.length === 0) return null;

  const COLOR = {
    OK: "text-green-400",
    DONE: "text-green-400",
    SUCCESS: "text-green-400",
    FAIL: "text-red-400",
    ERROR: "text-red-400",
    WARN: "text-yellow-400",
    WARNING: "text-yellow-400",
    SKIP: "text-gray-400",
    INFO: "text-blue-400",
  };

  return (
    <div
      className={`mt-3 rounded border text-xs font-mono overflow-auto max-h-52 ${
        isDark
          ? "bg-gray-900 border-gray-700 text-gray-300"
          : "bg-gray-50 border-gray-200 text-gray-700"
      }`}
    >
      <div className="p-2 space-y-px">
        {steps.map((step, i) => {
          const s = (step.status || "INFO").toUpperCase();
          const color = COLOR[s] || "text-blue-400";
          return (
            <div key={i} className="flex gap-2 items-start leading-relaxed min-w-0">
              <span className={`font-bold flex-shrink-0 w-14 ${color}`}>{s}</span>
              <span
                className={`font-semibold flex-shrink-0 w-36 truncate ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {step.label || ""}
              </span>
              <span
                className={`break-words min-w-0 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {step.message || ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResultBanner({ result, theme }) {
  const isDark = theme === "dark";
  if (!result) return null;

  const ok = !!result.ok;
  const message = result.message || (ok ? "Success." : "Failed.");
  const errors = Array.isArray(result.errors) ? result.errors : [];

  return (
    <div
      className={`mt-3 p-3 rounded-lg border flex items-start gap-2 text-sm ${
        ok
          ? isDark
            ? "bg-green-900/30 border-green-700 text-green-300"
            : "bg-green-50 border-green-300 text-green-800"
          : isDark
            ? "bg-red-900/30 border-red-700 text-red-300"
            : "bg-red-50 border-red-300 text-red-800"
      }`}
    >
      <FontAwesomeIcon
        icon={ok ? faCheckCircle : faExclamationTriangle}
        className={`mt-0.5 flex-shrink-0 ${ok ? "text-green-500" : "text-red-500"}`}
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold break-words">{message}</p>
        {errors.length > 0 && (
          <ul className="mt-1 text-xs list-disc list-inside space-y-0.5 opacity-90">
            {errors.map((e, i) => (
              <li key={i} className="break-words">{e}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CopyableValue({ value, theme }) {
  const [copied, setCopied] = useState(false);
  const isDark = theme === "dark";

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <span
      className={`inline-flex items-start gap-1 font-mono text-xs px-2 py-0.5 rounded max-w-full ${
        isDark ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-800"
      }`}
    >
      <span className="break-all min-w-0">{value}</span>
      <button
        type="button"
        onClick={handleCopy}
        title="Copy"
        className="ml-1 opacity-60 hover:opacity-100 flex-shrink-0"
      >
        <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} className="text-xs" />
      </button>
    </span>
  );
}

function FieldRow({ label, children, theme }) {
  const isDark = theme === "dark";
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <label
        className={`text-xs font-medium uppercase tracking-wide ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}
      >
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function KvPanel({ title, data, theme }) {
  const isDark = theme === "dark";
  const entries = Object.entries(data || {}).filter(
    ([, v]) => v !== null && v !== "" && v !== false && v !== undefined
  );
  if (entries.length === 0) return null;

  return (
    <div
      className={`rounded p-3 space-y-1.5 text-xs border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`}
    >
      <p className={`font-semibold mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        {title}
      </p>
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-3 items-start min-w-0">
          <span
            className={`w-40 flex-shrink-0 pt-1 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {k}
          </span>
          <div className="min-w-0">
            <CopyableValue value={String(v)} theme={theme} />
          </div>
        </div>
      ))}
    </div>
  );
}

function JsonPanel({ title, data, theme }) {
  const [open, setOpen] = useState(false);
  const isDark = theme === "dark";
  if (!data) return null;

  return (
    <div
      className={`rounded border text-xs ${
        isDark ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 font-semibold ${
          isDark
            ? "text-gray-300 hover:bg-gray-800"
            : "text-gray-600 hover:bg-gray-100"
        }`}
      >
        <span>{title}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="text-xs"
        />
      </button>
      {open && (
        <pre
          className={`px-3 pb-3 overflow-auto max-h-52 font-mono whitespace-pre-wrap break-words ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function inputCls(isDark, { readOnly = false } = {}) {
  return [
    "w-full min-w-0 px-2.5 py-1.5 rounded border text-sm outline-none transition-colors",
    readOnly ? "opacity-60 cursor-not-allowed" : "",
    isDark
      ? "bg-gray-700 text-gray-100 border-gray-600 focus:ring-1 focus:ring-blue-500"
      : "bg-white text-gray-800 border-gray-300 focus:ring-1 focus:ring-blue-400",
  ]
    .filter(Boolean)
    .join(" ");
}

function checkboxCls() {
  return "w-4 h-4 accent-blue-500 cursor-pointer flex-shrink-0";
}

function CreateCustomerModal({
  open,
  onClose,
  theme,
  isDark,
  sourceOptions,
  ncForm,
  setNcForm,
  ncLoading,
  ncDryOk,
  ncDryResult,
  ncExecResult,
  handleNcDryRun,
  handleNcCreate,
  derivedDb,
  derivedUser,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 overflow-y-auto">
        <div className="min-h-full flex items-start justify-center px-3 sm:px-4 pt-6 sm:pt-10 pb-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="New Customer Provisioning"
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-5xl rounded-xl shadow-2xl border flex flex-col ${
              isDark
                ? "bg-gray-800 text-gray-100 border-gray-700"
                : "bg-white text-gray-800 border-gray-200"
            }`}
            style={{ maxHeight: "calc(100dvh - 32px)" }}
          >
            {/* Header */}
            <div
              className={`sticky top-0 z-20 flex items-start justify-between gap-3 px-4 sm:px-5 py-4 border-b rounded-t-xl ${
                isDark
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="min-w-0">
                <h3 className="text-base font-bold flex items-center gap-2 min-w-0">
                  <FontAwesomeIcon icon={faServer} className="text-blue-400 flex-shrink-0" />
                  <span className="truncate">New Customer Provisioning</span>
                </h3>
                <p className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Dry-run first to preview derived values and catch validation issues.
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close modal"
                className={`w-10 h-10 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors ${
                  isDark
                    ? "text-gray-300 hover:bg-gray-700 hover:text-white"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                }`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4 min-w-0">
              <FieldRow label="Source Subdomain (template tenant)" theme={theme}>
                {sourceOptions.length > 0 ? (
                  <select
                    value={ncForm.source_subdomain}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, source_subdomain: e.target.value }))
                    }
                    className={inputCls(isDark)}
                  >
                    <option value="">— select source tenant —</option>
                    {sourceOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={ncForm.source_subdomain}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, source_subdomain: e.target.value.trim() }))
                    }
                    placeholder="e.g. demo"
                    className={inputCls(isDark)}
                  />
                )}
              </FieldRow>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                <FieldRow label="Company Name" theme={theme}>
                  <input
                    type="text"
                    value={ncForm.company_name}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, company_name: e.target.value }))
                    }
                    placeholder="Acme Field Services"
                    className={inputCls(isDark)}
                  />
                </FieldRow>

                <FieldRow label="App Name" theme={theme}>
                  <input
                    type="text"
                    value={ncForm.app_name}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, app_name: e.target.value }))
                    }
                    placeholder="ogPumper"
                    className={inputCls(isDark)}
                  />
                </FieldRow>

                <FieldRow label="Target Subdomain" theme={theme}>
                  <input
                    type="text"
                    value={ncForm.target_subdomain}
                    onChange={(e) =>
                      setNcForm((f) => ({
                        ...f,
                        target_subdomain: e.target.value.trim().toLowerCase(),
                      }))
                    }
                    placeholder="acmeft"
                    className={inputCls(isDark)}
                  />
                </FieldRow>

                <FieldRow label="Initial Password" theme={theme}>
                  <input
                    type="password"
                    value={ncForm.target_pass}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, target_pass: e.target.value }))
                    }
                    placeholder="min 8 characters"
                    autoComplete="new-password"
                    className={inputCls(isDark)}
                  />
                </FieldRow>

                <FieldRow label="Docroot" theme={theme}>
                  <input
                    type="text"
                    value={ncForm.docroot}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, docroot: e.target.value }))
                    }
                    placeholder="public_html/live"
                    className={inputCls(isDark)}
                  />
                </FieldRow>
              </div>

              {ncForm.target_subdomain && (
                <div
                  className={`rounded p-3 space-y-2 text-xs border min-w-0 ${
                    isDark
                      ? "bg-gray-700/60 border-gray-600"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <p className={`font-semibold mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Derived values (server may adjust):
                  </p>
                  <div className="flex gap-3 items-start min-w-0">
                    <span className={`w-24 flex-shrink-0 pt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      DB Name
                    </span>
                    <div className="min-w-0">
                      <CopyableValue value={derivedDb} theme={theme} />
                    </div>
                  </div>
                  <div className="flex gap-3 items-start min-w-0">
                    <span className={`w-24 flex-shrink-0 pt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      DB User
                    </span>
                    <div className="min-w-0">
                      <CopyableValue value={derivedUser} theme={theme} />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm min-w-0">
                  <input
                    type="checkbox"
                    checked={ncForm.create_subdomain}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, create_subdomain: e.target.checked }))
                    }
                    className={checkboxCls()}
                  />
                  <span className="break-words">Create subdomain on hosting</span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer text-sm min-w-0">
                  <input
                    type="checkbox"
                    checked={ncForm.activate_now}
                    onChange={(e) =>
                      setNcForm((f) => ({ ...f, activate_now: e.target.checked }))
                    }
                    className={checkboxCls()}
                  />
                  <span className="break-words">Activate immediately</span>
                </label>
              </div>

              <ResultBanner result={ncDryResult} theme={theme} />
              <StepLog steps={ncDryResult?.steps} theme={theme} />

              {ncDryResult?.ok && ncDryResult?.data?.normalized && (
                <div
                  className={`rounded p-3 space-y-1.5 text-xs border min-w-0 ${
                    isDark
                      ? "bg-gray-700/60 border-gray-600"
                      : "bg-green-50 border-green-200"
                  }`}
                >
                  <p className={`font-semibold ${isDark ? "text-green-400" : "text-green-700"}`}>
                    Server-confirmed values:
                  </p>
                  {Object.entries(ncDryResult.data.normalized).map(([k, v]) =>
                    v ? (
                      <div key={k} className="flex gap-3 items-start min-w-0">
                        <span className={`w-32 flex-shrink-0 pt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {k}
                        </span>
                        <div className="min-w-0">
                          <CopyableValue value={String(v)} theme={theme} />
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              )}

              <JsonPanel title="Clients Row Preview" data={ncDryResult?.data?.preview} theme={theme} />

              <ResultBanner result={ncExecResult} theme={theme} />
              <StepLog steps={ncExecResult?.steps} theme={theme} />
              <KvPanel title="Provision Result" data={ncExecResult?.data?.result} theme={theme} />

              {ncExecResult?.ok && (
                <p className="text-green-400 text-sm font-semibold">
                  ✓ Customer provisioned successfully. Remember to update DNS if needed.
                </p>
              )}
            </div>

            {/* Footer */}
            <div
              className={`sticky bottom-0 z-20 px-4 sm:px-5 py-4 border-t rounded-b-xl ${
                isDark
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 rounded text-sm ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                  }`}
                >
                  Close
                </button>

                <button
                  type="button"
                  onClick={handleNcDryRun}
                  disabled={ncLoading || !ncForm.source_subdomain || !ncForm.target_subdomain}
                  className="px-4 py-2 rounded text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                >
                  {ncLoading && !ncDryOk ? <Spinner /> : null} Dry Run
                </button>

                <button
                  type="button"
                  onClick={handleNcCreate}
                  disabled={ncLoading || !ncDryOk}
                  title={!ncDryOk ? "Run a successful dry run first" : ""}
                  className="px-4 py-2 rounded text-sm font-medium bg-green-700 hover:bg-green-800 text-white disabled:opacity-50"
                >
                  {ncLoading && ncDryOk ? <Spinner /> : null} Create Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Tab definitions ────────────────────────────────────────────────────── */

const TABS = [
  { id: "clients", label: "Clients", icon: faUsers },
  { id: "jobs", label: "Recent Jobs", icon: faList },
  { id: "fieldticket", label: "FieldTicket Init", icon: faWrench },
  { id: "create", label: "New Customer", icon: faPlus },
];

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════════════════ */

export default function DatabaseManager({ tenantSubdomain, initialTab } = {}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  /* ── Token status ─────────────────────────────────────────────────────── */
  const [tokStatus, setTokStatus] = useState(() => tokenStatus());

  /* ── Tab — default to fieldticket when a tenant is pre-selected,
        to clients otherwise, unless caller specifies initialTab ─────────── */
  const [activeTab, setActiveTab] = useState(
    initialTab || (tenantSubdomain ? "fieldticket" : "clients")
  );

  /* ── Collapsed state (user can minimise the whole panel) ─────────────── */
  const [collapsed, setCollapsed] = useState(false);

  /* ── Shared clients list (used by Clients tab AND drop-downs) ────────── */
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState("");
  const [clientSearch, setClientSearch] = useState("");

  /* ── Jobs list ────────────────────────────────────────────────────────── */
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState("");

  /* ── FieldTicket init form ────────────────────────────────────────────── */
  const [ftForm, setFtForm] = useState({
    init_subdomain: "",
    create_fieldticket_subdomain: true,
  });
  const [ftLoading, setFtLoading] = useState(false);
  const [ftDryResult, setFtDryResult] = useState(null);
  const [ftDryOk, setFtDryOk] = useState(false);
  const [ftExecResult, setFtExecResult] = useState(null);

  /* ── New Customer form ────────────────────────────────────────────────── */
  const [ncForm, setNcForm] = useState({
    source_subdomain: "",
    company_name: "",
    app_name: "ogPumper",
    target_subdomain: "",
    target_pass: "",
    create_subdomain: true,
    activate_now: false,
    docroot: "public_html/live",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ncLoading, setNcLoading] = useState(false);
  const [ncDryResult, setNcDryResult] = useState(null);
  const [ncDryOk, setNcDryOk] = useState(false);
  const [ncExecResult, setNcExecResult] = useState(null);

  /* ── Core API call helper ─────────────────────────────────────────────── */

  const callApi = useCallback(async (action, payload = {}) => {
    const tok = sessionStorage.getItem("og_access_token") || "";
    const resp = await fetch(API_BASE, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tok}`,
      },
      body: JSON.stringify({ action, ...payload }),
    });

    const data = await resp.json().catch(() => ({
      ok: false,
      message: `HTTP ${resp.status} — unexpected response`,
    }));

    if (!resp.ok || !data.ok) {
      const err = Object.assign(
        new Error(data.message || `Request failed (HTTP ${resp.status})`),
        { data, status: resp.status }
      );
      throw err;
    }
    return data;
  }, []);

  /* ── Data fetchers ────────────────────────────────────────────────────── */

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    setClientsError("");
    try {
      const data = await callApi("list_clients");
      setClients(data.data?.clients || []);
    } catch (err) {
      setClientsError(err.message || "Failed to load clients.");
    } finally {
      setLoadingClients(false);
    }
  }, [callApi]);

  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    setJobsError("");
    try {
      const data = await callApi("list_recent_jobs", { limit: 25 });
      setJobs(data.data?.jobs || []);
    } catch (err) {
      setJobsError(err.message || "Failed to load jobs.");
    } finally {
      setLoadingJobs(false);
    }
  }, [callApi]);

  /* Seed form state from props on first mount only */
  useEffect(() => {
    if (tenantSubdomain) {
      setFtForm((f) => ({ ...f, init_subdomain: tenantSubdomain }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Auto-fetch when tab becomes active (and token is valid) */
  useEffect(() => {
    if (collapsed || tokStatus !== "ok") return;
    if (activeTab === "clients") fetchClients();
    if (activeTab === "jobs") fetchJobs();
    if (activeTab === "fieldticket" && clients.length === 0) fetchClients();
    if (activeTab === "create" && clients.length === 0) fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, collapsed, tokStatus]);

  /* ── FieldTicket helpers ──────────────────────────────────────────────── */

  const ftHostname = ftForm.init_subdomain
    ? ftForm.init_subdomain + FIELDTICKET_HOST_SUFFIX
    : "";

  async function handleFtDryRun() {
    setFtLoading(true);
    setFtDryResult(null);
    setFtDryOk(false);
    setFtExecResult(null);
    try {
      const data = await callApi("init_fieldticket_dry_run", {
        init_subdomain: ftForm.init_subdomain,
        create_fieldticket_subdomain: ftForm.create_fieldticket_subdomain,
      });
      setFtDryResult(data);
      setFtDryOk(!!data.ok);
    } catch (err) {
      setFtDryResult(err.data || { ok: false, message: err.message, errors: [], steps: [] });
      setFtDryOk(false);
    } finally {
      setFtLoading(false);
    }
  }

  async function handleFtExecute() {
    if (
      !window.confirm(
        `Initialize FieldTicket for "${ftForm.init_subdomain}"?\n\n` +
          `Hostname: ${ftHostname}\n` +
          `Docroot:  ${FIELDTICKET_DOCROOT}\n\n` +
          "This will create tables and optionally create the subdomain. Proceed?"
      )
    ) {
      return;
    }

    setFtLoading(true);
    setFtExecResult(null);
    try {
      const data = await callApi("init_fieldticket_create", {
        init_subdomain: ftForm.init_subdomain,
        create_fieldticket_subdomain: ftForm.create_fieldticket_subdomain,
      });
      setFtExecResult(data);
      if (data.ok) {
        setFtDryOk(false);
      }
    } catch (err) {
      setFtExecResult(err.data || { ok: false, message: err.message, errors: [], steps: [] });
    } finally {
      setFtLoading(false);
    }
  }

  /* ── New Customer helpers ─────────────────────────────────────────────── */

  async function handleNcDryRun() {
    setNcLoading(true);
    setNcDryResult(null);
    setNcDryOk(false);
    setNcExecResult(null);
    try {
      const data = await callApi("new_customer_dry_run", buildNcPayload(true));
      setNcDryResult(data);
      setNcDryOk(!!data.ok);
    } catch (err) {
      setNcDryResult(err.data || { ok: false, message: err.message, errors: [], steps: [] });
      setNcDryOk(false);
    } finally {
      setNcLoading(false);
    }
  }

  async function handleNcCreate() {
    if (
      !window.confirm(
        `Create new customer tenant "${ncForm.target_subdomain}"?\n\n` +
          `Source: ${ncForm.source_subdomain}\n` +
          `Company: ${ncForm.company_name}\n` +
          `App: ${ncForm.app_name}\n\n` +
          "This will provision a database, user, and optionally a subdomain. This cannot be undone. Proceed?"
      )
    ) {
      return;
    }

    setNcLoading(true);
    setNcExecResult(null);
    try {
      const data = await callApi("new_customer_create", buildNcPayload(true));
      setNcExecResult(data);
      if (data.ok) setNcDryOk(false);
    } catch (err) {
      setNcExecResult(err.data || { ok: false, message: err.message, errors: [], steps: [] });
    } finally {
      setNcLoading(false);
    }
  }

  function openCreateModal() {
    setNcDryResult(null);
    setNcDryOk(false);
    setNcExecResult(null);
    setShowCreateModal(true);
  }

  function closeCreateModal() {
    setShowCreateModal(false);
  }

  function buildNcPayload(includePass) {
    const p = {
      source_subdomain: ncForm.source_subdomain,
      target_subdomain: ncForm.target_subdomain,
      company_name: ncForm.company_name,
      app_name: ncForm.app_name,
      docroot: ncForm.docroot || "public_html/live",
      create_subdomain: ncForm.create_subdomain,
      activate_now: ncForm.activate_now,
    };
    if (includePass) p.target_pass = ncForm.target_pass;
    return p;
  }

  /* ── Derived values shown in New Customer form ───────────────────────── */

  function deriveDbName(subdomain) {
    if (!subdomain) return "";
    const safe = subdomain.replace(/[^a-z0-9_]/gi, "_").toLowerCase();
    const prefixed = `ogendeav_${safe}`;
    return prefixed.slice(0, 64);
  }

  function deriveDbUser(subdomain) {
    if (!subdomain) return "";
    const safe = subdomain.replace(/[^a-z0-9_]/gi, "_").toLowerCase();
    const prefixed = `ogendeav_${safe}`;
    return prefixed.slice(0, 47);
  }

  /* ══════════════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════════════ */

  function renderTokenBanner() {
    if (tokStatus === "ok") return null;
    return (
      <div
        className={`flex items-start gap-3 p-3 rounded-lg border mb-4 text-sm ${
          isDark
            ? "bg-yellow-900/30 border-yellow-700 text-yellow-300"
            : "bg-yellow-50 border-yellow-300 text-yellow-800"
        }`}
      >
        <FontAwesomeIcon icon={faKey} className="mt-0.5 flex-shrink-0 text-yellow-500" />
        <div>
          <p className="font-semibold">
            {tokStatus === "expired"
              ? "Admin access token has expired."
              : "No admin access token found."}
          </p>
          <p className="text-xs mt-0.5 opacity-80">
            Please log out and back in to obtain a new token (valid for 10 minutes).
            API calls will fail until the token is refreshed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setTokStatus(tokenStatus())}
          className="ml-auto text-xs underline opacity-70 hover:opacity-100 whitespace-nowrap"
        >
          Recheck
        </button>
      </div>
    );
  }

  function renderClientsTab() {
    const filtered = clients.filter((c) => {
      const q = clientSearch.toLowerCase();
      return (
        !q ||
        (c.subdomain || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q)
      );
    });

    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 max-w-xs">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-2.5 top-2 text-xs ${isDark ? "text-gray-400" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder="Filter clients…"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className={`pl-7 pr-3 py-1.5 rounded border text-sm w-full outline-none ${
                isDark
                  ? "bg-gray-700 text-gray-100 border-gray-600 focus:ring-1 focus:ring-blue-500"
                  : "bg-white text-gray-800 border-gray-300 focus:ring-1 focus:ring-blue-400"
              }`}
            />
          </div>
          <button
            type="button"
            onClick={fetchClients}
            disabled={loadingClients}
            className="px-3 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {loadingClients ? <Spinner /> : "Refresh"}
          </button>
        </div>

        {clientsError && (
          <p className="text-red-400 text-sm mb-2">{clientsError}</p>
        )}

        {!loadingClients && !clientsError && filtered.length === 0 && (
          <p className={`text-sm text-center py-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {clients.length === 0 ? "No clients loaded. Click Refresh." : "No matches."}
          </p>
        )}

        {filtered.length > 0 && (
          <div className={`overflow-x-auto rounded border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <table className={`w-full text-xs ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              <thead className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                <tr>
                  {["Subdomain", "Company", "App", "Active", "DB Name"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr
                    key={c.subdomain || i}
                    className={`border-t ${
                      isDark
                        ? "border-gray-700 hover:bg-gray-700/50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-3 py-1.5 font-mono font-semibold">{c.subdomain}</td>
                    <td className="px-3 py-1.5">{c.company}</td>
                    <td className="px-3 py-1.5">{c.app_name}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                          c.active === "Y"
                            ? "bg-green-700/50 text-green-300"
                            : "bg-gray-600/50 text-gray-400"
                        }`}
                      >
                        {c.active === "Y" ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className={`px-3 py-1.5 font-mono ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {c.db_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loadingClients && (
          <p className="text-center text-sm py-6 text-blue-400">
            <Spinner /> Loading clients…
          </p>
        )}
      </div>
    );
  }

  function renderJobsTab() {
    const STATUS_COLOR = {
      DONE: "text-green-400",
      SUCCESS: "text-green-400",
      COMPLETED: "text-green-400",
      FAILED: "text-red-400",
      ERROR: "text-red-400",
      IN_PROGRESS: "text-blue-400",
      RUNNING: "text-blue-400",
      PENDING: "text-yellow-400",
    };

    return (
      <div>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={fetchJobs}
            disabled={loadingJobs}
            className="px-3 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {loadingJobs ? <Spinner /> : "Refresh"}
          </button>
        </div>

        {jobsError && <p className="text-red-400 text-sm mb-2">{jobsError}</p>}

        {!loadingJobs && !jobsError && jobs.length === 0 && (
          <p className={`text-sm text-center py-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            No recent jobs found. Click Refresh.
          </p>
        )}

        {jobs.length > 0 && (
          <div className={`overflow-x-auto rounded border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <table className={`w-full text-xs ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              <thead className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                <tr>
                  {["ID", "Type", "Target DB", "Target User", "Status", "Created", "Message"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const statusKey = (j.status || "").toUpperCase();
                  const color = STATUS_COLOR[statusKey] || "text-gray-300";
                  return (
                    <tr
                      key={j.id}
                      className={`border-t ${
                        isDark
                          ? "border-gray-700 hover:bg-gray-700/50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-3 py-1.5 font-mono">{j.id}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {j.job_type === "NEW_CUSTOMER_CREATE"
                          ? "New Customer"
                          : j.job_type === "EXISTING_TENANT_INIT"
                            ? "FieldTicket Init"
                            : j.job_type}
                      </td>
                      <td className="px-3 py-1.5 font-mono">{j.target_db}</td>
                      <td className="px-3 py-1.5 font-mono">{j.target_user}</td>
                      <td className={`px-3 py-1.5 font-semibold ${color}`}>{j.status}</td>
                      <td className={`px-3 py-1.5 whitespace-nowrap ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {j.created_at ? j.created_at.slice(0, 16) : ""}
                      </td>
                      <td
                        className={`px-3 py-1.5 max-w-xs truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        title={j.message}
                      >
                        {j.message}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {loadingJobs && (
          <p className="text-center text-sm py-6 text-blue-400">
            <Spinner /> Loading jobs…
          </p>
        )}
      </div>
    );
  }

  function renderFieldTicketTab() {
    const subdomainOptions = clients.map((c) => c.subdomain).filter(Boolean);

    return (
      <div className="space-y-4 max-w-xl">
        <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Idempotently ensure FieldTicket tables (JobTypes, ItemTypes, JobItems,
          FieldTicket, FieldTicketDetail) on an existing tenant. Always dry-run first.
        </p>

        <FieldRow label="Tenant Subdomain" theme={theme}>
          {subdomainOptions.length > 0 ? (
            <select
              value={ftForm.init_subdomain}
              onChange={(e) =>
                setFtForm((f) => ({
                  ...f,
                  init_subdomain: e.target.value,
                }))
              }
              className={inputCls(isDark)}
            >
              <option value="">— select tenant —</option>
              {subdomainOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={ftForm.init_subdomain}
              onChange={(e) =>
                setFtForm((f) => ({ ...f, init_subdomain: e.target.value.trim() }))
              }
              placeholder="e.g. acmeft"
              className={inputCls(isDark)}
            />
          )}
        </FieldRow>

        {ftForm.init_subdomain && (
          <div
            className={`rounded p-3 space-y-2 text-xs border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex gap-3 items-start">
              <span className={`w-24 flex-shrink-0 font-medium pt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Hostname
              </span>
              <CopyableValue value={ftHostname} theme={theme} />
            </div>
            <div className="flex gap-3 items-start">
              <span className={`w-24 flex-shrink-0 font-medium pt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Docroot
              </span>
              <CopyableValue value={FIELDTICKET_DOCROOT} theme={theme} />
            </div>
          </div>
        )}

        <FieldRow label="Options" theme={theme}>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={ftForm.create_fieldticket_subdomain}
              onChange={(e) =>
                setFtForm((f) => ({
                  ...f,
                  create_fieldticket_subdomain: e.target.checked,
                }))
              }
              className={checkboxCls()}
            />
            Create / ensure FieldTicket subdomain on hosting
          </label>
        </FieldRow>

        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleFtDryRun}
            disabled={ftLoading || !ftForm.init_subdomain}
            className="px-4 py-2 rounded text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
          >
            {ftLoading && !ftDryOk ? <Spinner /> : null} Dry Run
          </button>
          <button
            type="button"
            onClick={handleFtExecute}
            disabled={ftLoading || !ftDryOk}
            title={!ftDryOk ? "Run a successful dry run first" : ""}
            className="px-4 py-2 rounded text-sm font-medium bg-green-700 hover:bg-green-800 text-white disabled:opacity-50"
          >
            {ftLoading && ftDryOk ? <Spinner /> : null} Run Init
          </button>
        </div>

        {!ftDryOk && ftDryResult && (
          <p className="text-xs text-yellow-400">
            Fix the errors above and re-run the dry run before executing.
          </p>
        )}

        <ResultBanner result={ftDryResult} theme={theme} />
        <StepLog steps={ftDryResult?.steps} theme={theme} />
        <KvPanel title="Init Preview" data={ftDryResult?.data?.preview} theme={theme} />
        <JsonPanel title="Schema Scan Report" data={ftDryResult?.data?.scan_report} theme={theme} />
        <ResultBanner result={ftExecResult} theme={theme} />
        <StepLog steps={ftExecResult?.steps} theme={theme} />
        <KvPanel title="Execution Result" data={ftExecResult?.data?.result} theme={theme} />
      </div>
    );
  }

  function renderCreateTab() {
    const sourceOptions = clients.map((c) => c.subdomain).filter(Boolean);
    const derivedDb = deriveDbName(ncForm.target_subdomain);
    const derivedUser = deriveDbUser(ncForm.target_subdomain);

    return (
      <div>
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            New Customer
          </button>
        </div>

        <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
          Click "New Customer" to open the provisioning form. Always run a dry
          run before creating to preview derived values and catch validation errors.
        </p>

        <CreateCustomerModal
          open={showCreateModal}
          onClose={closeCreateModal}
          theme={theme}
          isDark={isDark}
          sourceOptions={sourceOptions}
          ncForm={ncForm}
          setNcForm={setNcForm}
          ncLoading={ncLoading}
          ncDryOk={ncDryOk}
          ncDryResult={ncDryResult}
          ncExecResult={ncExecResult}
          handleNcDryRun={handleNcDryRun}
          handleNcCreate={handleNcCreate}
          derivedDb={derivedDb}
          derivedUser={derivedUser}
        />
      </div>
    );
  }

  /* ── Panel frame ──────────────────────────────────────────────────────── */

  return (
    <div
      className={`rounded-xl border shadow-sm mb-6 ${
        isDark
          ? "bg-gray-800 border-gray-700"
          : "bg-white border-gray-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className={`w-full flex items-center justify-between px-5 py-3 rounded-t-xl text-left ${
          isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
        }`}
      >
        <span className="flex items-center gap-2 font-bold text-sm tracking-wide">
          <FontAwesomeIcon
            icon={faServer}
            className="text-blue-400"
          />
          Database Manager
          <span
            className={`text-xs font-normal px-2 py-0.5 rounded-full ml-1 ${
              isDark
                ? "bg-blue-900/50 text-blue-300"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            Admin
          </span>
        </span>
        <FontAwesomeIcon
          icon={collapsed ? faChevronDown : faChevronUp}
          className={`text-xs ${isDark ? "text-gray-400" : "text-gray-400"}`}
        />
      </button>

      {!collapsed && (
        <div className="px-5 pb-5">
          {renderTokenBanner()}

          <div
            className={`flex gap-1 mb-4 border-b pb-0 overflow-x-auto ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? isDark
                      ? "bg-blue-700/30 text-blue-300 border-b-2 border-blue-500"
                      : "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                    : isDark
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FontAwesomeIcon icon={t.icon} className="text-xs" />
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "clients" && renderClientsTab()}
          {activeTab === "jobs" && renderJobsTab()}
          {activeTab === "fieldticket" && renderFieldTicketTab()}
          {activeTab === "create" && renderCreateTab()}
        </div>
      )}
    </div>
  );
}